import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { CrmService } from '../../core/services/crm.service';
import { OrcamentosService } from '../../core/services/orcamentos.service';
import { WorkflowsService } from '../../core/services/workflows.service';
import {
  LeadContatoPlanejado,
  Orcamento,
  OrcamentoAtendimentoStatus,
  WorkflowDetalhe,
  WorkflowEtapa,
  WorkflowEtapaTipo,
} from '../../core/models/workflow.models';
import { LeadContatoModalComponent, LeadContatoPayload } from '../../shared/components/lead-contato-modal.component';

interface ContatoHojeViewLocal {
  contato: LeadContatoPlanejado;
  orcamento?: Orcamento;
}

type RiskType = 'ATRASO' | 'PARADO' | 'SEM_CONTATO' | 'PECAS';

interface RiskItemView {
  tipo: RiskType;
  referencia: string;
  cliente: string;
  veiculo: string;
  etapaOuStatus: string;
  responsavel: string;
  dias: number;
  workflowId?: number;
  orcamentoId?: number;
}

interface StageCardView {
  etapa: string;
  total: number;
  atrasados: number;
  parados: number;
  mediaDias: number;
}

interface OrcamentoResumoStatus {
  status: OrcamentoAtendimentoStatus;
  total: number;
  valor: number;
}

interface EtapaLentaView {
  etapa: string;
  media: number;
  p95: number;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, FormsModule, LeadContatoModalComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit, OnDestroy {
  activeTab: 'prioridades' | 'operacao' | 'crm' | 'resumo' = 'prioridades';

  filtroData = this.toInputDate(new Date());
  filtroSeguradora = '';
  filtroConsultor = '';
  filtroResponsavel = '';
  filtroSomenteRisco = true;

  seguradoras: string[] = [];
  consultores: string[] = [];
  responsaveis: string[] = [];

  workflows: WorkflowDetalhe[] = [];
  orcamentos: Orcamento[] = [];
  contatos: LeadContatoPlanejado[] = [];

  contatosHoje: ContatoHojeViewLocal[] = [];
  topRiscos: RiskItemView[] = [];
  wipPorEtapa: StageCardView[] = [];
  filaPecas: WorkflowDetalhe[] = [];
  funilResumo: OrcamentoResumoStatus[] = [];
  orcamentosSemContato: Orcamento[] = [];
  orcamentosRisco: Array<Orcamento & { diasSemContato: number }> = [];
  etapasLentas: EtapaLentaView[] = [];
  maioresParados: Array<WorkflowDetalhe & { diasParado: number }> = [];

  kpiSemContato = 0;
  kpiContatosHoje = 0;
  kpiWip = 0;
  kpiWipAtraso = 0;
  kpiParados = 0;
  kpiEntregas3Dias = 0;
  kpiEntradas7Dias = 0;
  kpiSaidas7Dias = 0;
  kpiSaldo7Dias = 0;

  modalContatoAberta = false;
  selectedLead: Orcamento | null = null;
  leadContacts: Array<{ leadId: number | string; tipo: string; obs: string; dataHora: string }> = [];
  gerarOsOpen = false;
  selectedOsLead: Orcamento | null = null;
  osNumero = '';

  private subscriptions = new Subscription();

  constructor(
    private workflowsService: WorkflowsService,
    private orcamentosService: OrcamentosService,
    private crmService: CrmService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest([
        this.workflowsService.getAllDetails(),
        this.orcamentosService.getAll(),
        this.crmService.getAll(),
      ]).subscribe(([workflows, orcamentos, contatos]) => {
        this.workflows = workflows;
        this.orcamentos = orcamentos;
        this.contatos = contatos;
        this.refreshFilterOptions();
        this.rebuild();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setTab(tab: 'prioridades' | 'operacao' | 'crm' | 'resumo'): void {
    this.activeTab = tab;
  }

  onFiltersChange(): void {
    this.rebuild();
  }

  marcarContatoConcluido(id: number): void {
    this.crmService.marcarContatoConcluido(id);
  }

  abrirModalContato(item: ContatoHojeViewLocal): void {
    const lead = item.orcamento ?? this.findLeadFromSnapshot(item.contato.orcamentoId);
    if (!lead) {
      return;
    }
    this.selectedLead = lead;
    this.modalContatoAberta = true;
  }

  abrirModalGerarOS(item: ContatoHojeViewLocal): void {
    const lead = item.orcamento ?? this.findLeadFromSnapshot(item.contato.orcamentoId);
    if (!lead) {
      return;
    }
    this.selectedOsLead = lead;
    this.osNumero = '';
    this.gerarOsOpen = true;
  }

  fecharModalContato(): void {
    this.modalContatoAberta = false;
    this.selectedLead = null;
  }

  fecharModalGerarOS(): void {
    this.gerarOsOpen = false;
    this.selectedOsLead = null;
  }

  confirmarGerarOS(): void {
    if (!this.selectedOsLead) {
      return;
    }
    this.orcamentosService.converterParaOS(this.selectedOsLead.id);
    this.fecharModalGerarOS();
  }

  salvarContato(payload: LeadContatoPayload): void {
    const registro = {
      leadId: payload.leadId,
      tipo: payload.tipo,
      obs: payload.obs,
      dataHora: new Date().toISOString(),
    };
    this.leadContacts = [...this.leadContacts, registro];
    this.fecharModalContato();
  }

  encerrarContato(): void {
    this.fecharModalContato();
  }

  goToWorkflow(id: number): void {
    this.router.navigate(['/workflow', id]);
  }

  goToCrm(): void {
    this.router.navigate(['/crm']);
  }

  abrirItemRisco(item: RiskItemView): void {
    if (item.workflowId) {
      this.goToWorkflow(item.workflowId);
      return;
    }
    this.goToCrm();
  }

  getBadgeClass(tipo: RiskType): string {
    switch (tipo) {
      case 'ATRASO':
        return 'badge badge--danger';
      case 'PARADO':
        return 'badge badge--warn';
      case 'SEM_CONTATO':
        return 'badge badge--neutral';
      case 'PECAS':
        return 'badge badge--info';
      default:
        return 'badge';
    }
  }

  getStatusClass(status: OrcamentoAtendimentoStatus): string {
    switch (status) {
      case 'Aberto':
        return 'status-chip status-chip--aberto';
      case 'Aguardando retorno':
        return 'status-chip status-chip--retorno';
      case 'Efetuar Contato':
        return 'status-chip status-chip--contato';
      case 'Perdido':
        return 'status-chip status-chip--perdido';
      case 'Ganho':
        return 'status-chip status-chip--ganho';
      default:
        return 'status-chip';
    }
  }

  getContatoCanal(item: ContatoHojeViewLocal): string {
    return `${item.contato.canal} - ${this.pad(item.contato.dataHora.getHours())}:${this.pad(
      item.contato.dataHora.getMinutes()
    )}`;
  }

  getVeiculoLabel(workflow?: WorkflowDetalhe): string {
    if (!workflow) {
      return '—';
    }
    return `${workflow.placa} · ${workflow.marca} ${workflow.modelo}`;
  }

  getOrcamentoNumero(orcamento: Orcamento): string {
    return orcamento.codigoExterno || `ORC-${orcamento.id}`;
  }

  private rebuild(): void {
    const refDate = this.parseInputDate(this.filtroData);
    const startDay = this.startOfDay(refDate);
    const endDay = this.endOfDay(refDate);
    const plus3 = this.addDays(endDay, 3);

    const workflows = this.getFilteredWorkflows();
    const orcamentos = this.getFilteredOrcamentos();

    this.contatosHoje = this.contatos
      .filter(
        (contato) =>
          !contato.concluido &&
          contato.dataHora >= startDay &&
          contato.dataHora <= endDay
      )
      .map((contato) => ({
        contato,
        orcamento: orcamentos.find((item) => item.id === contato.orcamentoId),
      }));

    this.orcamentosSemContato = orcamentos.filter((orcamento) => this.isSemContato(orcamento, refDate));

    const wip = workflows.filter((workflow) => !this.isFinalizado(workflow));
    const wipAtrasados = wip.filter((workflow) => this.getAtrasoDias(workflow, refDate) > 0);
    const wipParados = wip.filter((workflow) => this.getDiasParado(workflow, refDate) >= 3);

    this.kpiSemContato = this.orcamentosSemContato.length;
    this.kpiContatosHoje = this.contatosHoje.length;
    this.kpiWip = wip.length;
    this.kpiWipAtraso = wipAtrasados.length;
    this.kpiParados = wipParados.length;
    this.kpiEntregas3Dias = workflows.filter((workflow) => {
      const prevista = this.getDataFinalPrevista(workflow);
      return prevista && prevista >= startDay && prevista <= plus3;
    }).length;

    this.topRiscos = this.buildTopRiscos(workflows, orcamentos, refDate);

    this.wipPorEtapa = this.buildWipPorEtapa(wip, refDate);

    this.filaPecas = wip.filter((workflow) => this.isEtapaPecas(workflow));

    this.funilResumo = this.buildFunilResumo(orcamentos);

    this.orcamentosRisco = this.orcamentosSemContato
      .map((item) => ({ ...item, diasSemContato: this.getDiasSemContato(item, refDate) }))
      .sort((a, b) => b.diasSemContato - a.diasSemContato)
      .slice(0, 8);

    this.buildResumoSemanal(workflows, refDate);

    this.etapasLentas = this.buildEtapasLentas(workflows);

    this.maioresParados = wip
      .map((workflow) => ({ ...workflow, diasParado: this.getDiasParado(workflow, refDate) }))
      .sort((a, b) => b.diasParado - a.diasParado)
      .slice(0, 5);
  }

  private buildTopRiscos(workflows: WorkflowDetalhe[], orcamentos: Orcamento[], refDate: Date): RiskItemView[] {
    const itens: Array<RiskItemView & { score: number }> = [];

    workflows.forEach((workflow) => {
      const atrasoDias = this.getAtrasoDias(workflow, refDate);
      const paradosDias = this.getDiasParado(workflow, refDate);
      const diasEtapa = this.getDiasNaEtapa(workflow, refDate);

      if (atrasoDias > 0) {
        itens.push({
          tipo: 'ATRASO',
          referencia: workflow.osNumero,
          cliente: workflow.clienteNome,
          veiculo: this.getVeiculoLabel(workflow),
          etapaOuStatus: this.getEtapaNome(workflow),
          responsavel: workflow.responsavelAtual,
          dias: atrasoDias,
          workflowId: workflow.id,
          score: atrasoDias * 3,
        });
      }

      if (paradosDias >= 3) {
        itens.push({
          tipo: 'PARADO',
          referencia: workflow.osNumero,
          cliente: workflow.clienteNome,
          veiculo: this.getVeiculoLabel(workflow),
          etapaOuStatus: this.getEtapaNome(workflow),
          responsavel: workflow.responsavelAtual,
          dias: paradosDias,
          workflowId: workflow.id,
          score: paradosDias * 2,
        });
      }

      if (this.isEtapaPecas(workflow)) {
        itens.push({
          tipo: 'PECAS',
          referencia: workflow.osNumero,
          cliente: workflow.clienteNome,
          veiculo: this.getVeiculoLabel(workflow),
          etapaOuStatus: this.getEtapaNome(workflow),
          responsavel: workflow.responsavelAtual,
          dias: diasEtapa,
          workflowId: workflow.id,
          score: diasEtapa,
        });
      }
    });

    orcamentos.forEach((orcamento) => {
      if (!this.isSemContato(orcamento, refDate)) {
        return;
      }
      const diasSemContato = this.getDiasSemContato(orcamento, refDate);
      itens.push({
        tipo: 'SEM_CONTATO',
        referencia: this.getOrcamentoNumero(orcamento),
        cliente: orcamento.clienteNome,
        veiculo: `${orcamento.placa} · ${orcamento.marca} ${orcamento.modelo}`,
        etapaOuStatus: orcamento.statusAtendimento ?? 'Aberto',
        responsavel: orcamento.consultorResponsavel || orcamento.consultor || '—',
        dias: diasSemContato,
        orcamentoId: orcamento.id,
        score: diasSemContato * 2,
      });
    });

    return itens
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
      .map(({ score, ...rest }) => rest);
  }

  private buildWipPorEtapa(wip: WorkflowDetalhe[], refDate: Date): StageCardView[] {
    const mapa = new Map<string, StageCardView & { dias: number[] }>();

    wip.forEach((workflow) => {
      const etapa = this.getEtapaNome(workflow);
      const atrasoDias = this.getAtrasoDias(workflow, refDate) > 0 ? 1 : 0;
      const paradoDias = this.getDiasParado(workflow, refDate) >= 3 ? 1 : 0;
      const diasEtapa = this.getDiasNaEtapa(workflow, refDate);

      if (!mapa.has(etapa)) {
        mapa.set(etapa, {
          etapa,
          total: 0,
          atrasados: 0,
          parados: 0,
          mediaDias: 0,
          dias: [],
        });
      }

      const current = mapa.get(etapa)!;
      current.total += 1;
      current.atrasados += atrasoDias;
      current.parados += paradoDias;
      current.dias.push(diasEtapa);
    });

    const list = Array.from(mapa.values()).map(({ dias, ...rest }) => ({
      ...rest,
      mediaDias: dias.length ? Math.round(dias.reduce((sum, val) => sum + val, 0) / dias.length) : 0,
    }));

    const filtered = this.filtroSomenteRisco
      ? list.filter((item) => item.atrasados > 0 || item.parados > 0)
      : list;

    return filtered.sort((a, b) => b.total - a.total);
  }

  private buildFunilResumo(orcamentos: Orcamento[]): OrcamentoResumoStatus[] {
    const statuses: OrcamentoAtendimentoStatus[] = [
      'Aberto',
      'Aguardando retorno',
      'Efetuar Contato',
      'Perdido',
      'Ganho',
    ];

    return statuses.map((status) => {
      const items = orcamentos.filter((item) => item.statusAtendimento === status);
      const total = items.length;
      const valor = items.reduce((sum, item) => sum + (item.valorOrcamento ?? item.valor ?? 0), 0);
      return { status, total, valor };
    });
  }

  private buildResumoSemanal(workflows: WorkflowDetalhe[], refDate: Date): void {
    const end = this.endOfDay(refDate);
    const start = this.startOfDay(this.addDays(refDate, -6));

    const entradas = workflows.filter((workflow) => {
      const inicio = workflow.criadoEm;
      return inicio >= start && inicio <= end;
    });

    const saidas = workflows.filter((workflow) => {
      const real = this.getDataFinalReal(workflow);
      return real && real >= start && real <= end;
    });

    this.kpiEntradas7Dias = entradas.length;
    this.kpiSaidas7Dias = saidas.length;
    this.kpiSaldo7Dias = this.kpiEntradas7Dias - this.kpiSaidas7Dias;
  }

  private buildEtapasLentas(workflows: WorkflowDetalhe[]): EtapaLentaView[] {
    const mapa = new Map<string, number[]>();

    workflows.forEach((workflow) => {
      workflow.etapas.forEach((etapa) => {
        if (!etapa.inicio || !etapa.fim) {
          return;
        }
        const dias = this.diffDays(etapa.fim, etapa.inicio);
        if (!mapa.has(etapa.nome)) {
          mapa.set(etapa.nome, []);
        }
        mapa.get(etapa.nome)!.push(dias);
      });
    });

    const list = Array.from(mapa.entries()).map(([etapa, valores]) => {
      const media = valores.length
        ? Math.round(valores.reduce((sum, val) => sum + val, 0) / valores.length)
        : 0;
      const p95 = this.calcP95(valores);
      return { etapa, media, p95 };
    });

    return list.sort((a, b) => b.media - a.media).slice(0, 3);
  }

  private getFilteredWorkflows(): WorkflowDetalhe[] {
    return this.workflows.filter((workflow) => {
      if (this.filtroSeguradora && workflow.seguradoraNome !== this.filtroSeguradora) {
        return false;
      }
      if (this.filtroResponsavel && workflow.responsavelAtual !== this.filtroResponsavel) {
        return false;
      }
      return true;
    });
  }

  private getFilteredOrcamentos(): Orcamento[] {
    return this.orcamentos.filter((orcamento) => {
      if (this.filtroSeguradora && orcamento.seguradoraNome !== this.filtroSeguradora) {
        return false;
      }
      if (this.filtroConsultor) {
        const consultor = orcamento.consultorResponsavel || orcamento.consultor;
        if (consultor !== this.filtroConsultor) {
          return false;
        }
      }
      return true;
    });
  }

  private refreshFilterOptions(): void {
    this.seguradoras = this.unique([
      ...this.workflows.map((workflow) => workflow.seguradoraNome).filter(Boolean),
      ...this.orcamentos.map((orcamento) => orcamento.seguradoraNome).filter(Boolean),
    ] as string[]);

    this.consultores = this.unique(
      this.orcamentos
        .map((orcamento) => orcamento.consultorResponsavel || orcamento.consultor)
        .filter(Boolean) as string[]
    );

    this.responsaveis = this.unique(
      this.workflows.map((workflow) => workflow.responsavelAtual).filter(Boolean) as string[]
    );
  }

  private isSemContato(orcamento: Orcamento, refDate: Date): boolean {
    const status = orcamento.statusAtendimento;
    if (!status) {
      return false;
    }
    if (!['Aberto', 'Aguardando retorno', 'Efetuar Contato'].includes(status)) {
      return false;
    }
    return this.getDiasSemContato(orcamento, refDate) >= 2;
  }

  private getDiasSemContato(orcamento: Orcamento, refDate: Date): number {
    const base = orcamento.ultimoContatoEm ?? orcamento.dataCriacao ?? orcamento.criadoEm;
    return this.diffDays(refDate, base);
  }

  private isFinalizado(workflow: WorkflowDetalhe): boolean {
    return (
      workflow.etapaAtual === WorkflowEtapaTipo.FINALIZADO ||
      workflow.statusGeral === 'CONCLUIDO'
    );
  }

  getEtapaNome(workflow: WorkflowDetalhe): string {
    const etapa = workflow.etapas.find((item) => item.tipo === workflow.etapaAtual);
    return etapa?.nome ?? workflow.etapaAtual;
  }

  private getEtapaAtual(workflow: WorkflowDetalhe): WorkflowEtapa | undefined {
    return workflow.etapas.find((item) => item.tipo === workflow.etapaAtual);
  }

  private getDataFinalPrevista(workflow: WorkflowDetalhe): Date | null {
    if (workflow.dataPrevistaEntrega) {
      return workflow.dataPrevistaEntrega;
    }
    const prevista = workflow.etapas
      .map((etapa) => etapa.previstaFim)
      .filter(Boolean)
      .reduce<Date | null>((max, atual) => {
        if (!atual) {
          return max;
        }
        if (!max || atual > max) {
          return atual;
        }
        return max;
      }, null);

    return prevista ?? null;
  }

  private getDataFinalReal(workflow: WorkflowDetalhe): Date | null {
    const finalizado = workflow.etapas.find((etapa) => etapa.tipo === WorkflowEtapaTipo.FINALIZADO);
    if (finalizado?.fim) {
      return finalizado.fim;
    }
    const ultimaFim = workflow.etapas
      .map((etapa) => etapa.fim)
      .filter(Boolean)
      .reduce<Date | null>((max, atual) => {
        if (!atual) {
          return max;
        }
        if (!max || atual > max) {
          return atual;
        }
        return max;
      }, null);

    return ultimaFim ?? null;
  }

  private getAtrasoDias(workflow: WorkflowDetalhe, refDate: Date): number {
    if (this.isFinalizado(workflow)) {
      const prevista = this.getDataFinalPrevista(workflow);
      const real = this.getDataFinalReal(workflow);
      if (!prevista || !real) {
        return 0;
      }
      return Math.max(0, this.diffDays(real, prevista));
    }

    const prevista = this.getDataFinalPrevista(workflow);
    if (!prevista || refDate <= prevista) {
      return 0;
    }
    return Math.max(0, this.diffDays(refDate, prevista));
  }

  getDiasNaEtapa(workflow: WorkflowDetalhe, refDate: Date): number {
    const etapaAtual = this.getEtapaAtual(workflow);
    if (etapaAtual?.inicio) {
      return this.diffDays(refDate, etapaAtual.inicio);
    }
    return this.diffDays(refDate, workflow.criadoEm);
  }

  private getDiasParado(workflow: WorkflowDetalhe, refDate: Date): number {
    const etapaAtual = this.getEtapaAtual(workflow);
    const datas: Date[] = [];
    if (etapaAtual?.inicio) {
      datas.push(etapaAtual.inicio);
    }
    workflow.historico.forEach((item) => datas.push(item.dataHora));
    workflow.comunicacoes.forEach((item) => datas.push(item.dataHora));
    workflow.etapas.forEach((etapa) => {
      etapa.anexos.forEach((anexo) => datas.push(anexo.dataHora));
    });
    if (workflow.ultimoContatoEm) {
      datas.push(workflow.ultimoContatoEm);
    }
    const ultima = datas.sort((a, b) => b.getTime() - a.getTime())[0] ?? workflow.criadoEm;
    return this.diffDays(refDate, ultima);
  }

  private isEtapaPecas(workflow: WorkflowDetalhe): boolean {
    if (workflow.etapaAtual === WorkflowEtapaTipo.COMPRA_PECAS) {
      return true;
    }
    const etapa = this.getEtapaNome(workflow).toLowerCase();
    return etapa.includes('pe—a') || etapa.includes('peca');
  }

  private diffDays(a: Date, b: Date): number {
    const diff = a.getTime() - b.getTime();
    return Math.max(0, Math.floor(diff / (24 * 60 * 60 * 1000)));
  }

  private calcP95(values: number[]): number {
    if (!values.length) {
      return 0;
    }
    const sorted = [...values].sort((a, b) => a - b);
    const index = Math.ceil(0.95 * sorted.length) - 1;
    return sorted[Math.max(0, index)];
  }

  private unique(values: string[]): string[] {
    return Array.from(new Set(values)).sort();
  }

  parseInputDate(value: string): Date {
    if (!value) {
      return new Date();
    }
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private toInputDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
  }

  private endOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
  }

  private addDays(date: Date, days: number): Date {
    const next = new Date(date.getTime());
    next.setDate(next.getDate() + days);
    return next;
  }

  private pad(value: number): string {
    return value.toString().padStart(2, '0');
  }

  private findLeadFromSnapshot(id?: number): Orcamento | null {
    if (!id) {
      return null;
    }
    const found = this.orcamentosService.getSnapshot().find((item) => item.id === id);
    return found ?? null;
  }
}



